import type { IntegrationView } from "@planerius/contracts/public-api"
import {
  EVENT_REQUIRED_SCOPE,
  type WebhookDeliveryList,
  type WebhookEndpointCreated,
  type WebhookEndpointView,
  type WebhookEventType,
} from "@planerius/contracts/public-api-webhooks"
import type { ReactNode } from "react"
import { useState } from "react"

import { useCabinetAccess } from "../../access/use-access"
import {
  createWebhook,
  fetchDeliveries,
  fetchIntegrations,
  fetchWebhooks,
  retryFailedDeliveries,
  setWebhookState,
  testWebhook,
} from "../../api/integrations.api"
import { isApiProblem } from "../../api/problem"
import { JournalDialog } from "../../journal/appointment-dialogs"
import { cx } from "../../lib/cx"
import { countOf } from "../../lib/plural"
import { resolveBranch } from "../../shell/use-branch"
import { ProblemScreen } from "../../shell/states"
import { Badge, Button, Checkbox, Chip, Chips, Skeleton, TextField } from "../../ui"
import { useResource } from "../directories/use-resource"
import { EVENT_GROUPS, EVENT_LABELS, SCOPE_CHOICES, hookCard, momentWords } from "./developers-model"
import styles from "./developers.module.css"

/**
 * 4-В8 «Уведомления о событиях» — /developers/$id/webhooks (макет «Доступы, вебхуки,
 * печать - П2», карточка 640).
 *
 * Главное состояние — «отключено нами после суток молчания»: адрес, который сутки отвечает
 * ошибкой, мы выключаем сами и пишем, что сделать. Новый адрес не получает событий, пока
 * не ответил успешно на проверочное — защита от подписки чужого сервера на поток чужих данных.
 */
export function WebhooksScreen(props: { readonly integrationId: string; readonly onBack: () => void }): ReactNode {
  const { context } = useCabinetAccess()
  const timeZone = resolveBranch(context, null)?.timezone ?? context.tenant.defaultTimezone
  const [tick, setTick] = useState(0)
  const integrations = useResource((signal) => fetchIntegrations(signal), "integrations:webhooks")
  const hooks = useResource((signal) => fetchWebhooks(props.integrationId, signal), `webhooks:${props.integrationId}:${String(tick)}`)
  const integration = integrations.data?.items.find((item) => item.id === props.integrationId) ?? null
  const [adding, setAdding] = useState(false)
  const [created, setCreated] = useState<WebhookEndpointCreated | null>(null)
  const [openDeliveries, setOpenDeliveries] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [messages, setMessages] = useState<Readonly<Record<string, { readonly tone: "ok" | "error"; readonly text: string }>>>({})

  if (hooks.problem !== null && hooks.data === null) {
    return <ProblemScreen problem={hooks.problem} onRetry={hooks.reload} />
  }
  const reload = (): void => {
    setTick((value) => value + 1)
  }
  const say = (id: string, tone: "ok" | "error", text: string): void => {
    setMessages((value) => ({ ...value, [id]: { tone, text } }))
  }

  const act = async (hook: WebhookEndpointView, kind: "pause" | "resume" | "test" | "disable" | "retry"): Promise<void> => {
    setBusy(`${hook.id}:${kind}`)
    try {
      if (kind === "test") {
        const outcome = await testWebhook(props.integrationId, hook.id)
        say(
          hook.id,
          outcome.test.ok ? "ok" : "error",
          outcome.test.ok
            ? "Проверочное событие дошло. Адрес получает события."
            : `Проверочное событие не дошло. ${outcome.test.error ?? ""} Подписку пока не включаем — сначала сервер должен ответить успешно.`,
        )
      } else if (kind === "retry") {
        const outcome = await retryFailedDeliveries(props.integrationId, hook.id)
        say(
          hook.id,
          "ok",
          outcome.requeued === 0
            ? "Неудачных доставок за неделю нет — переотправлять нечего."
            : `Поставили в очередь заново: ${countOf(outcome.requeued, "событие", "события", "событий")}.`,
        )
      } else {
        await setWebhookState(props.integrationId, hook.id, kind)
        setMessages((value) => ({ ...value, [hook.id]: { tone: "ok", text: "" } }))
      }
      reload()
    } catch (error) {
      if (!isApiProblem(error)) throw error
      say(hook.id, "error", error.detail ?? error.title)
    } finally {
      setBusy(null)
    }
  }

  const items = hooks.data?.items ?? []

  return (
    <div className={styles.screen}>
      <div className={styles.crumbs}>
        <button type="button" className={styles.crumbLink} onClick={props.onBack}>
          Разработчикам
        </button>
        <span className={styles.crumbSep}>/</span>
        <span className={styles.crumbTitle}>{integration?.name ?? "Интеграция"}</span>
      </div>
      <div className={styles.page}>
        <section className={cx(styles.card, styles.narrow)} aria-labelledby="hooks-title">
          <div className={styles.head}>
            <div className={styles.headText}>
              <h1 id="hooks-title" className={styles.title}>
                Уведомления о событиях
              </h1>
              <div className={styles.sub}>Чужая программа узнаёт о новых записях · только https</div>
            </div>
            <Button
              size="sm"
              variant="secondary"
              deniedReason={integration !== null && integration.state !== "active" ? "У интеграции нет действующего доступа" : undefined}
              onClick={() => setAdding(true)}
            >
              + Адрес
            </Button>
          </div>

          {hooks.data === null ? (
            <Skeleton width="100%" height={160} />
          ) : items.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.strong}>Адресов нет</div>
              <div className={styles.sub}>
                Добавьте адрес, если программе подрядчика нужно узнавать о записях сразу, а не спрашивать каждые
                несколько минут.
              </div>
            </div>
          ) : (
            items.map((hook) => {
              const card = hookCard(hook, timeZone)
              const message = messages[hook.id]
              return (
                <article
                  key={hook.id}
                  className={cx(styles.item, card.tone === "error" && styles.itemAlert)}
                  aria-label={hook.url}
                >
                  <div className={styles.itemHead}>
                    <div className={styles.headText}>
                      <div className={styles.url}>{hook.url}</div>
                      <div className={styles.itemDev}>{hook.description ?? `секрет …${hook.secretLast4}`}</div>
                    </div>
                    <Badge tone={card.tone}>{card.state}</Badge>
                  </div>
                  <div className={styles.badges}>
                    {hook.eventTypes.map((event) => (
                      <Badge key={event} tone="gray">
                        {EVENT_LABELS[event]}
                      </Badge>
                    ))}
                  </div>
                  {card.note === null ? null : (
                    <div className={cx(styles.note, card.noteTone === "danger" && styles.noteDanger)}>{card.note}</div>
                  )}
                  <div className={cx(styles.facts, styles.factsSmall)}>
                    <div>Последняя удачная доставка: {card.last}</div>
                    <div>
                      Неудач подряд:{" "}
                      <b className={hook.consecutiveFailures > 0 ? styles.danger : styles.muted}>
                        {String(hook.consecutiveFailures)}
                      </b>
                    </div>
                  </div>
                  {message === undefined || message.text.length === 0 ? null : (
                    <div className={message.tone === "ok" ? styles.success : styles.problem} role="status">
                      {message.text}
                    </div>
                  )}
                  <div className={styles.actions}>
                    {card.action === null ? null : (
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={busy === `${hook.id}:${card.action.kind}`}
                        onClick={() => void act(hook, card.action?.kind ?? "test")}
                      >
                        {card.action.label}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setOpenDeliveries((value) => (value === hook.id ? null : hook.id))}
                    >
                      Последние доставки
                    </Button>
                    <Button size="sm" variant="ghost" loading={busy === `${hook.id}:retry`} onClick={() => void act(hook, "retry")}>
                      Переотправить неудачные
                    </Button>
                    {hook.status === "disabled" ? null : (
                      <Button size="sm" variant="danger-ghost" loading={busy === `${hook.id}:disable`} onClick={() => void act(hook, "disable")}>
                        Отключить
                      </Button>
                    )}
                  </div>
                  {openDeliveries === hook.id ? (
                    <Deliveries integrationId={props.integrationId} endpointId={hook.id} timeZone={timeZone} />
                  ) : null}
                </article>
              )
            })
          )}
          <div className={styles.hint}>
            Один адрес дважды не подписывается — удвоится поток. Адрес, который сутки не отвечает, мы отключаем сами и
            пишем здесь, что сделать.
          </div>
        </section>
      </div>

      {adding && integration !== null ? (
        <AddHookDialog
          integration={integration}
          branches={context.branches}
          onClose={() => setAdding(false)}
          onCreated={(result) => {
            setAdding(false)
            setCreated(result)
            reload()
          }}
        />
      ) : null}
      {created === null ? null : <SecretDialog created={created} onClose={() => setCreated(null)} />}
    </div>
  )
}

function Deliveries(props: { readonly integrationId: string; readonly endpointId: string; readonly timeZone: string }): ReactNode {
  const list = useResource(
    (signal): Promise<WebhookDeliveryList> => fetchDeliveries(props.integrationId, props.endpointId, signal),
    `deliveries:${props.endpointId}`,
  )
  if (list.data === null) return <Skeleton width="100%" height={60} />
  if (list.data.items.length === 0) return <div className={styles.hint}>Доставок ещё не было.</div>
  return (
    <div className={styles.rows}>
      {list.data.items.map((item) => {
        const failed = item.status === "failed" || item.status === "dead" || item.status === "dropped"
        return (
          <div key={item.deliveryId} className={styles.row}>
            <span className={styles.rowTime}>{momentWords(item.createdAt, props.timeZone)}</span>
            <span className={styles.rowRoute}>
              {EVENT_LABELS[item.eventType]} · попытка {String(item.attempt)}
            </span>
            <span className={cx(styles.rowOutcome, failed && styles.rowFailed)}>
              {DELIVERY_STATUS[item.status]}
              {item.responseStatus === null ? "" : ` · ${String(item.responseStatus)}`}
            </span>
          </div>
        )
      })}
    </div>
  )
}

const DELIVERY_STATUS: Readonly<Record<WebhookDeliveryList["items"][number]["status"], string>> = {
  pending: "в очереди",
  in_flight: "отправляется",
  succeeded: "доставлено",
  failed: "не дошло, повторим",
  dead: "не дошло",
  dropped: "сервер отказался",
}

/**
 * «+ Адрес»: события — только в пределах прав доступа (недоступные — серые с подсказкой),
 * филиалы — внутри филиалов доступа. Листа в макете нет (вопросы дизайнеру 75–77).
 */
function AddHookDialog(props: {
  readonly integration: IntegrationView
  readonly branches: readonly { readonly id: string; readonly name: string }[]
  readonly onClose: () => void
  readonly onCreated: (result: WebhookEndpointCreated) => void
}): ReactNode {
  const scopes = props.integration.access?.scopes ?? []
  const grantBranches = props.integration.access?.branchIds ?? []
  const branches = grantBranches.length === 0 ? props.branches : props.branches.filter((branch) => grantBranches.includes(branch.id))
  const [url, setUrl] = useState("https://")
  const [description, setDescription] = useState("")
  const [events, setEvents] = useState<readonly WebhookEventType[]>([])
  const [branchIds, setBranchIds] = useState<readonly string[]>([])
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const submit = async (): Promise<void> => {
    if (!url.startsWith("https://")) {
      setProblem("Адрес должен начинаться с https:// — по незащищённому соединению данные клиентов уйдут открытым текстом.")
      return
    }
    if (events.length === 0) {
      setProblem("Отметьте события: подписки «на всё» нет.")
      return
    }
    setBusy(true)
    setProblem(null)
    try {
      props.onCreated(
        await createWebhook(props.integration.id, {
          url: url.trim(),
          ...(description.trim().length > 0 ? { description: description.trim() } : {}),
          eventTypes: [...events],
          branchIds: [...branchIds],
        }),
      )
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    } finally {
      setBusy(false)
    }
  }

  return (
    <JournalDialog title="Адрес для уведомлений" onClose={props.onClose}>
      <div className={styles.group}>
        <TextField label="Адрес" value={url} inputMode="url" onChange={(event) => setUrl(event.target.value)} />
        <TextField
          label="Подпись"
          hint="Чтобы узнать адрес в списке: «Касса · записи → чек»."
          value={description}
          maxLength={200}
          onChange={(event) => setDescription(event.target.value)}
        />
        {EVENT_GROUPS.map((group) => (
          <div key={group.title} className={styles.group}>
            <div className={styles.groupLabel}>{group.title}</div>
            {group.events.map((event) => {
              const needed = EVENT_REQUIRED_SCOPE[event]
              const allowed = needed === null || scopes.includes(needed)
              const neededTitle = SCOPE_CHOICES.find((choice) => choice.scope === needed)?.title ?? needed ?? ""
              return (
                <Checkbox
                  key={event}
                  checked={events.includes(event)}
                  disabled={!allowed}
                  onChange={(checked) => setEvents((value) => (checked ? [...value, event] : value.filter((item) => item !== event)))}
                >
                  {EVENT_LABELS[event]}
                  {allowed ? null : <span className={styles.scopeSub}> — нужно право «{neededTitle}»</span>}
                </Checkbox>
              )
            })}
          </div>
        ))}
        {branches.length > 1 ? (
          <div className={styles.group}>
            <div className={styles.groupLabel}>События филиала</div>
            <Chips label="Филиалы">
              <Chip active={branchIds.length === 0} onClick={() => setBranchIds([])}>
                Все филиалы доступа
              </Chip>
              {branches.map((branch) => (
                <Chip
                  key={branch.id}
                  active={branchIds.includes(branch.id)}
                  onClick={() =>
                    setBranchIds((value) => (value.includes(branch.id) ? value.filter((id) => id !== branch.id) : [...value, branch.id]))
                  }
                >
                  Только {branch.name}
                </Chip>
              ))}
            </Chips>
          </div>
        ) : null}
        {problem === null ? null : (
          <div className={styles.problem} role="alert">
            {problem}
          </div>
        )}
        <div className={styles.actionsEnd}>
          <Button size="sm" variant="secondary" onClick={props.onClose}>
            Отмена
          </Button>
          <Button size="sm" variant="primary" loading={busy} onClick={() => void submit()}>
            Добавить и проверить
          </Button>
        </div>
      </div>
    </JournalDialog>
  )
}

/** Секрет подписи — один раз, как ключ (4-В7); рядом — итог проверки адреса. */
function SecretDialog(props: { readonly created: WebhookEndpointCreated; readonly onClose: () => void }): ReactNode {
  const [copied, setCopied] = useState(false)
  const { created } = props
  return (
    <JournalDialog title="Адрес добавлен" onClose={props.onClose}>
      <div className={styles.group}>
        <div className={styles.secret}>
          <div className={styles.secretLabel}>Секрет подписи · показывается один раз</div>
          <div className={styles.secretValue}>{created.secret}</div>
          <div className={styles.codeRow}>
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                void navigator.clipboard.writeText(created.secret).then(() => setCopied(true))
              }}
            >
              {copied ? "Скопировано" : "Скопировать"}
            </Button>
            <span>Им программа подрядчика проверяет, что событие пришло от нас.</span>
          </div>
        </div>
        <div className={created.test.ok ? styles.success : styles.problem} role="status">
          {created.test.ok
            ? "Проверочное событие дошло — адрес получает события."
            : `Проверочное событие не дошло. ${created.test.error ?? ""} Подписку пока не включаем — сначала сервер должен ответить успешно.`}
        </div>
        <div className={styles.actionsEnd}>
          <Button size="sm" variant="secondary" onClick={props.onClose}>
            {copied ? "Готово" : "Закрыть без копирования"}
          </Button>
        </div>
      </div>
    </JournalDialog>
  )
}
